/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import PropTypes from 'prop-types';
import {
  DragDropContext,
  Droppable,
  Draggable,
} from '@atlaskit/pragmatic-drag-and-drop-react-beautiful-dnd-migration';
import { Button, Grid, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import { mealWithArraySectionMoved } from '../../services/meals';
import { useTracker } from '../../operations/tracking_operations';
import { useUpdateMealMutation } from '../../app/smorgAppSyncGraphQLApi';
import { useGetUserProfileQuery } from '../../app/smorgAppSyncGraphQLApiCustomHooks';
import EditableLabelAndInput from '../common/editable_label_and_input';
import { arraySectionFullTextIsHeading } from '../../services/ingredients';
import IngredientHeading from './ingredient_heading';

const PreparationList = ({
  meal,
  readOnly,
  savingInProgress,
  currentlyEditingInline,
  onInlineEditFocus,
  onInlineEditChange,
  onInlineEditBlur,
  onRemoveItem,
  onAddItem,
}) => {
  const tracker = useTracker();

  const recipe = meal && meal.recipes[0];

  const { data: userProfile } = useGetUserProfileQuery();

  const [updateMeal] = useUpdateMealMutation();

  const handleDragEnd = (result) => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }
    const updatedMeal = mealWithArraySectionMoved(
      meal,
      'preparations',
      result.source.index,
      result.destination.index,
    );
    updateMeal(updatedMeal);
    tracker(userProfile, ['Edit meal'], ['numMealsEdited']);
  };

  const disabled = readOnly || savingInProgress;

  const onPaste = (event, preparationID) => {
    const clipboardData = event.clipboardData || window.clipboardData;
    const pastedText = clipboardData.getData('Text');
    onInlineEditChange('preparations', preparationID, pastedText);
    onInlineEditBlur('preparations', preparationID);
  };

  return (
    <>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="ingredientsDroppable">
          {(droppableProvided) => (
            <div
              {...droppableProvided.droppableProps}
              ref={droppableProvided.innerRef}>
              {recipe.preparations.map((preparation, index) => {
                const isEditing =
                  currentlyEditingInline &&
                  currentlyEditingInline.section === 'preparations' &&
                  currentlyEditingInline.itemId === preparation.id;
                const isHeading = arraySectionFullTextIsHeading(
                  preparation.fullText,
                );
                return (
                  <Draggable
                    key={preparation.id}
                    draggableId={preparation.id}
                    isDragDisabled={readOnly}
                    index={index}>
                    {(provided) => (
                      <Grid
                        container
                        ref={provided.innerRef}
                        {...provided.draggableProps}>
                        <Grid item xs={11} sm={9}>
                          <Grid container>
                            <Grid
                              item
                              sx={{ display: { xs: 'none', sm: 'block' } }}
                              sm={1}>
                              &nbsp;
                            </Grid>
                            <Grid
                              item
                              xs={1}
                              maxWidth="40px !important"
                              flexShrink={0}
                              flexGrow={0}
                              style={{ paddingTop: 10 }}>
                              <span {...provided.dragHandleProps}>
                                {!readOnly && <DragHandleIcon />}
                              </span>
                            </Grid>
                            <Grid item xs={10} style={{ display: 'flex' }}>
                              {isHeading && !isEditing ? (
                                <IngredientHeading
                                  ingredient={preparation}
                                  onClick={() =>
                                    !disabled &&
                                    onInlineEditFocus(
                                      'preparations',
                                      preparation.id,
                                    )
                                  }
                                />
                              ) : (
                                <EditableLabelAndInput
                                  edit={isEditing}
                                  multiline
                                  defaultValue={preparation.fullText}
                                  labelPlaceholder="Add preparation instruction"
                                  disabled={disabled}
                                  textStyle={{
                                    margin: 'auto 0',
                                  }}
                                  inputStyle={{
                                    width: '100%',
                                  }}
                                  onFocus={() =>
                                    onInlineEditFocus &&
                                    onInlineEditFocus(
                                      'preparations',
                                      preparation.id,
                                    )
                                  }
                                  onChange={(value) =>
                                    onInlineEditChange(
                                      'preparations',
                                      preparation.id,
                                      value,
                                    )
                                  }
                                  onDismiss={(reason) =>
                                    onInlineEditBlur(
                                      'preparations',
                                      preparation.id,
                                      reason,
                                    )
                                  }
                                  onPaste={(event) =>
                                    onPaste(event, preparation.id)
                                  }
                                />
                              )}
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid
                          item
                          xs={1}
                          sm={1}
                          justifyContent="flex-end"
                          flexShrink={0}
                          flexGrow={0}
                          style={{ paddingTop: 10 }}>
                          {!readOnly && (
                            <IconButton
                              onClick={() => onRemoveItem(preparation.id)}
                              variant="contained"
                              size="small"
                              disableElevation>
                              <DeleteIcon color="error" />
                            </IconButton>
                          )}
                        </Grid>
                      </Grid>
                    )}
                  </Draggable>
                );
              })}
              {droppableProvided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      {!readOnly && (
        <Grid container>
          <Grid item xs={9}>
            <Grid container>
              <Grid item xs={1}>
                &nbsp;
              </Grid>
              <Grid item xs={9}>
                <Button
                  onClick={onAddItem}
                  variant="contained"
                  size="medium"
                  color="secondary"
                  disableElevation
                  style={{ justifyContent: 'flex-start' }}>
                  Add instruction
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}
    </>
  );
};

PreparationList.propTypes = {
  meal: PropTypes.shape({
    recipes: PropTypes.arrayOf(
      PropTypes.shape({
        preparations: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
      }),
    ),
  }).isRequired,
  readOnly: PropTypes.bool.isRequired,
  savingInProgress: PropTypes.bool.isRequired,
  currentlyEditingInline: PropTypes.shape({
    section: PropTypes.string.isRequired,
    itemId: PropTypes.string,
  }).isRequired,
  onInlineEditFocus: PropTypes.func.isRequired,
  onInlineEditChange: PropTypes.func.isRequired,
  onInlineEditBlur: PropTypes.func.isRequired,
  onRemoveItem: PropTypes.func.isRequired,
  onAddItem: PropTypes.func.isRequired,
};

export default PreparationList;
